<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.groups',
    path: 'groups',
    component: 'Groups',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Groups',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const items = ref(null);
  const groupTypes = ref([
    {
      id: 'meter',
      text: $fn.value.pgettext('GroupType', 'Meter'),
    },
    {
      id: 'client',
      text: $fn.value.pgettext('GroupType', 'Company'),
    },
  ]);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'inactive',
      type: 'checkbox',
      header: $fn.value.gettext('Inactive'),
      width: '90px',
      textAlign: 'center',
    },
    {
      name: 'group_type',
      type: 'select',
      header: $fn.value.gettext('Group Type'),
      width: '160px',
      required: true,
      options: groupTypes.value,
    },
    {
      name: 'name',
      header: $fn.value.gettext('Name'),
      width: '140px',
      required: true,
    },
    {
      name: 'description',
      header: $fn.value.gettext('Description'),
      width: '320px',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    groupTypes: groupTypes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = () => {
    return $fn.value.fetch('list_group').then((p) => p.data);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'groupTypes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3343864469950044_columns', nodeId: '3343864469950044' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      items,
      groupTypes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_9gnyh51wu_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_3343864469950044_script_eykfnjb4k_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor($fn.value.crud('group').upsert($event.data.row));
  };
  const act_3343864469950044_script_eykfnjb4k_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_eykfnjb4k_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_rxvfghimq_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor($fn.value.crud('group').delete($event.data.row));
  };
  const act_3343864469950044_script_rxvfghimq_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_rxvfghimq_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_9gnyh51wu =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_9gnyh51wu_getActionArgs,
    });
  const inst_evh_7315092382398562_close_zn9h95yln =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_9gnyh51wu,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_zn9h95yln],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_qvrw9bfib =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tdjo4rbay =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_qvrw9bfib,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tdjo4rbay],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_qb32jtne5 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_v19uw2w0c =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_qb32jtne5,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_v19uw2w0c],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_eykfnjb4k = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_eykfnjb4k_getActionArgs,
  });
  const inst_evh_3343864469950044_save_rgt2hvxal =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_eykfnjb4k,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_rgt2hvxal],
    event: 'save',
  });
  const act_3343864469950044_script_rxvfghimq = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_rxvfghimq_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_30x7rgj0t =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_rxvfghimq,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_30x7rgj0t],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_zn9h95yln,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_tdjo4rbay,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_v19uw2w0c,
    reload: inst_evh_8320016629450276_clickPreventStop_v19uw2w0c,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_3343864469950044_save_rgt2hvxal,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_30x7rgj0t,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_zn9h95yln = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_zn9h95yln.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_tdjo4rbay = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tdjo4rbay.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_v19uw2w0c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_v19uw2w0c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_rgt2hvxal = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_rgt2hvxal.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_30x7rgj0t = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_30x7rgj0t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Groups"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ gettext('Groups') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.Groups"
        editable
        :isRowEditable="(p) => !p.locked"
        :value="items"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
